import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Mail, Phone, MapPin, ArrowUpRight, ShieldCheck } from 'lucide-react';
import api from '../services/api';
import { useApi } from '../hooks/useApi';
import BrandLogo from './common/BrandLogo';
import branding from '@/config/branding';

export const Footer = () => {
  const { data: settings } = useApi('settings', api.settings.get);
  return (
    <footer className="bg-[#080808] text-white border-t border-[#222222]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-4 space-y-6">
            <Link to="/" className="inline-block transition-transform hover:scale-105">
              <BrandLogo 
                context="footer"
                src={settings?.logo_url || branding.logo} 
                alt={`${settings?.company_name || branding.fullName} - ${branding.tagline}`}
              />
            </Link>

            <div className="space-y-2">
              <p className="text-xs font-semibold tracking-widest uppercase text-[#C9A227]">
                {branding.tagline}
              </p>
              <p className="text-gray-400 text-sm leading-relaxed max-w-sm">
                {settings?.slogan || "Structuration patrimoniale, investissement immobilier haut de gamme et accompagnement sur-mesure pour la diaspora et investisseurs institutionnels."}
              </p>
            </div>

            <div className="flex items-center space-x-3 pt-2">
              {settings?.facebook && (
                <a href={settings.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 bg-white/5 hover:bg-[#C9A227] hover:text-[#080808] text-gray-400 rounded-lg flex items-center justify-center transition-all border border-white/10 hover:border-[#C9A227]">
                  <Facebook className="w-4 h-4" />
                </a>
              )}
              {settings?.instagram && (
                <a href={settings.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 bg-white/5 hover:bg-[#C9A227] hover:text-[#080808] text-gray-400 rounded-lg flex items-center justify-center transition-all border border-white/10 hover:border-[#C9A227]">
                  <Instagram className="w-4 h-4" />
                </a>
              )}
              {settings?.linkedin && (
                <a href={settings.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-9 h-9 bg-white/5 hover:bg-[#C9A227] hover:text-[#080808] text-gray-400 rounded-lg flex items-center justify-center transition-all border border-white/10 hover:border-[#C9A227]">
                  <Linkedin className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>

          {/* Navigation */}
          <div className="lg:col-span-3">
            <h3 className="text-xs font-bold tracking-widest text-[#C9A227] uppercase mb-6">Navigation</h3>
            <ul className="space-y-3">
              <li><Link to="/" className="text-gray-400 hover:text-white transition-colors text-sm">Accueil</Link></li>
              <li><Link to="/catalogue" className="text-gray-400 hover:text-white transition-colors text-sm">Catalogue des Biens</Link></li>
              <li><Link to="/programmes" className="text-gray-400 hover:text-white transition-colors text-sm">Programmes Neufs</Link></li>
              <li><Link to="/investisseurs" className="text-gray-400 hover:text-white transition-colors text-sm">Investisseurs & Diaspora</Link></li>
              <li><Link to="/carte" className="text-gray-400 hover:text-white transition-colors text-sm">Carte Interactive</Link></li>
              <li><Link to="/comparateur" className="text-gray-400 hover:text-white transition-colors text-sm">Comparateur de Biens</Link></li>
              <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors text-sm">Actualités & Analyses</Link></li>
              <li><Link to="/faq" className="text-gray-400 hover:text-white transition-colors text-sm">Foire Aux Questions</Link></li>
            </ul>
          </div>

          {/* Expertise */}
          <div className="lg:col-span-2">
            <h3 className="text-xs font-bold tracking-widest text-[#C9A227] uppercase mb-6">Expertise</h3>
            <ul className="space-y-3">
              <li className="text-gray-400 text-sm">Résidences de Prestige</li>
              <li className="text-gray-400 text-sm">Programmes Neufs (R+9 à R+20)</li>
              <li className="text-gray-400 text-sm">Structuration Juridique</li>
              <li className="text-gray-400 text-sm">Family Office & Patrimoine</li>
              <li className="text-gray-400 text-sm">Gestion Locative Premium</li>
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <h3 className="text-xs font-bold tracking-widest text-[#C9A227] uppercase mb-6">Siège & Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin className="w-4 h-4 text-[#C9A227] flex-shrink-0 mt-1" />
                <span className="text-gray-400 text-sm leading-relaxed whitespace-pre-line">
                  {settings?.address || branding.address}
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="w-4 h-4 text-[#C9A227] flex-shrink-0" />
                <a href={`tel:${settings?.phone?.replace(/\s/g, '') || '+221338000000'}`} className="text-gray-400 hover:text-[#C9A227] transition-colors text-sm">
                  {settings?.phone || branding.phone}
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="w-4 h-4 text-[#C9A227] flex-shrink-0" />
                <a href={`mailto:${settings?.email || branding.email}`} className="text-gray-400 hover:text-[#C9A227] transition-colors text-sm">
                  {settings?.email || branding.email}
                </a>
              </li>
              <li className="pt-2">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/5 border border-white/10 text-xs text-gray-400">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#C9A227]" />
                  <span>Sécurisation Notariale 100%</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-[#222222] flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-xs">
            {branding.copyright}
          </p>
          <div className="flex items-center space-x-6 text-xs text-gray-400">
            <Link to="/mentions-legales" className="hover:text-[#C9A227] transition-colors">
              Mentions légales
            </Link>
            <Link to="/faq" className="hover:text-[#C9A227] transition-colors">
              Confidentialité
            </Link>
            <Link to="/contact" className="hover:text-[#C9A227] transition-colors">
              Support
            </Link>
            <Link to="/admin/login" className="hover:text-[#C9A227] transition-colors flex items-center gap-1 text-[#C9A227]/80">
              <span>Portail Admin</span>
              <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
